import type { SessionHistoryResponse, TeamHistory } from '../types'
import { CATEGORY_STYLES } from '../lib/categoryColors'

type Category = keyof typeof CATEGORY_STYLES

interface ActivityMeta {
  id: string
  name: string
  category: string
}

interface Props {
  history: SessionHistoryResponse
  catalog: ActivityMeta[]
}

const CATEGORY_ORDER: Category[] = ['Technology', 'Innovation', 'People', 'ChangeManagement', 'Governance']

const CATEGORY_LABEL: Record<Category, string> = {
  People: 'People & Skills',
  ChangeManagement: 'Change Management',
  Technology: 'Technology & Capability',
  Innovation: 'Innovation & Deployment',
  Governance: 'Governance & Risk',
}

export default function TeamsActivityTimeline({ history, catalog }: Props) {
  const byId = new Map(catalog.map((a) => [a.id, a as ActivityMeta]))

  // Union of program ids ever active or fired as one-shot across all teams.
  const unionIds = new Set<string>()
  for (const tid of history.team_order) {
    const team = history.teams[tid]
    if (!team) continue
    for (const s of team.snapshots) {
      for (const id of s.active_programs) unionIds.add(id)
      for (const id of s.decision?.one_shots ?? []) unionIds.add(id)
    }
  }

  const rows = [...unionIds]
    .map((id) => byId.get(id))
    .filter((a): a is ActivityMeta => Boolean(a))
    .sort((a, b) => {
      const ca = CATEGORY_ORDER.indexOf(a.category as Category)
      const cb = CATEGORY_ORDER.indexOf(b.category as Category)
      if (ca !== cb) return ca - cb
      return a.id.localeCompare(b.id)
    })

  const teams = history.team_order
    .map((tid) => history.teams[tid])
    .filter((t): t is TeamHistory => Boolean(t))

  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic">
        No programs were started or fired yet.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      <Legend />
      {teams.map((team) => (
        <TeamGantt
          key={team.team_id}
          team={team}
          rows={rows}
          numQuarters={history.num_quarters}
        />
      ))}
    </div>
  )
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
      {CATEGORY_ORDER.map((cat) => (
        <div key={cat} className="flex items-center gap-1.5">
          <span aria-hidden className={`inline-block w-2.5 h-2.5 rounded-sm ${CATEGORY_STYLES[cat].dot}`} />
          <span>{CATEGORY_LABEL[cat]}</span>
        </div>
      ))}
      <span className="mx-2 text-slate-400">·</span>
      <span>Filled block = program active · Dot = used this quarter</span>
      <span className="mx-2 text-slate-400">·</span>
      <span className="inline-flex items-center gap-1">
        <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" />
        Board grant
      </span>
      <span className="inline-flex items-center gap-1">
        <span aria-hidden className="inline-block w-2.5 h-2.5 rounded-sm bg-red-400" />
        Board denial
      </span>
    </div>
  )
}

function TeamGantt({
  team, rows, numQuarters,
}: {
  team: TeamHistory
  rows: ActivityMeta[]
  numQuarters: number
}) {
  const byQ = new Map(team.snapshots.map((s) => [s.quarter, s]))
  const finalSnap = team.snapshots[team.snapshots.length - 1]
  const finalValue = finalSnap?.scorecard?.value_est_m

  const CELL_W = 22

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-2">
        <h3 className="text-base font-semibold">{team.team_name}</h3>
        {finalValue != null && (
          <span className="text-sm text-slate-500">
            ${finalValue.toFixed(1)}M · {team.snapshots.length}q played
          </span>
        )}
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <div className="inline-block min-w-max">
          <div className="flex items-center bg-slate-50 border-b border-slate-200">
            <div className="w-48 shrink-0 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
              Activity
            </div>
            {Array.from({ length: numQuarters }, (_, i) => {
              const q = i + 1
              const marker = q % 5 === 0
              return (
                <div
                  key={q}
                  style={{ width: CELL_W }}
                  className={`text-center text-[10px] font-mono ${marker ? 'text-slate-700 font-semibold' : 'text-slate-400'}`}
                >
                  {q}
                </div>
              )
            })}
          </div>

          {rows.map((activity) => {
            const style = CATEGORY_STYLES[activity.category as Category]
            return (
              <div key={activity.id} className="flex items-center border-b border-slate-100 last:border-b-0">
                <div
                  className="w-48 shrink-0 px-2 py-0.5 text-xs flex items-center gap-1.5 truncate"
                  title={activity.name}
                >
                  <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${style?.dot ?? 'bg-slate-300'}`} />
                  <span className="font-mono text-slate-500 shrink-0">{activity.id}</span>
                  <span className="text-slate-700 truncate">{activity.name}</span>
                </div>
                {Array.from({ length: numQuarters }, (_, i) => {
                  const q = i + 1
                  const snap = byQ.get(q)
                  const active = snap?.active_programs.includes(activity.id) ?? false
                  const oneShot = snap?.decision?.one_shots.includes(activity.id) ?? false
                  const title = `${activity.id} ${activity.name} · Q${q}${active ? ' · active' : oneShot ? ' · fired' : ''}`
                  return (
                    <div
                      key={q}
                      style={{ width: CELL_W, height: 18 }}
                      className="flex items-center justify-center"
                      title={title}
                    >
                      {active ? (
                        <div className={`w-full h-full ${style?.dot ?? 'bg-slate-300'}`} />
                      ) : oneShot ? (
                        <div className={`w-2.5 h-2.5 rounded-full ${style?.dot ?? 'bg-slate-300'}`} />
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )
          })}

          <div className="flex items-center bg-slate-50 border-t border-slate-200">
            <div className="w-48 shrink-0 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
              Urgency
            </div>
            {Array.from({ length: numQuarters }, (_, i) => {
              const q = i + 1
              const snap = byQ.get(q)
              const tempo = snap?.decision?.tempo ?? null
              return (
                <div
                  key={q}
                  style={{ width: CELL_W, height: 18 }}
                  className="text-center text-[10px] font-mono text-slate-600 flex items-center justify-center"
                  title={tempo != null ? `Q${q} · Urgency ${tempo.toFixed(1)}×` : `Q${q}`}
                >
                  {tempo != null ? (tempo === 1 ? '1' : tempo.toFixed(1).replace(/\.0$/, '')) : '—'}
                </div>
              )
            })}
          </div>

          <div className="flex items-center bg-slate-50 border-t border-slate-200">
            <div className="w-48 shrink-0 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
              Board Asks
            </div>
            {Array.from({ length: numQuarters }, (_, i) => {
              const q = i + 1
              const outcome = byQ.get(q)?.board_request_outcome ?? null
              const label = outcome === 'granted'
                ? `Q${q} · Granted (+25% for 4q)`
                : outcome === 'denied'
                ? `Q${q} · Denied (−8 Confidence)`
                : `Q${q} · no ask`
              return (
                <div
                  key={q}
                  style={{ width: CELL_W, height: 18 }}
                  className="flex items-center justify-center"
                  title={label}
                >
                  {outcome === 'granted' ? (
                    <div className="w-3 h-3 rounded-sm bg-emerald-500" />
                  ) : outcome === 'denied' ? (
                    <div className="w-3 h-3 rounded-sm bg-red-400" />
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
